import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Subject } from 'rxjs';
import { ListGeometry } from './list-geometry';

/** Rebuild the rendered range once less than this much rendered content remains past a viewport edge. */
const MIN_BUFFER_PX = 1000;
/** How far past each viewport edge to render, so rebuilds stay far apart. */
const MAX_BUFFER_PX = 1800;
/**
 * Height used for a bidlist card before anything is measured. Only affects the first frame or two;
 * the mean of the real measurements takes over after that. Tuned for the bidlist's card layout —
 * re-measure this if it is reused for a list with a different row shape.
 */
export const DEFAULT_ITEM_SIZE_PX = 290;

export class MeasuredSizeVirtualScrollStrategy implements VirtualScrollStrategy {
  private viewport: CdkVirtualScrollViewport | null = null;
  private readonly geometry: ListGeometry;
  private viewportWidth = 0;
  private renderedRange: ListRange = { start: 0, end: 0 };
  private emittedIndex = -1;
  private updateScheduled = false;
  private pendingFrame = 0;
  /** Whether detach() has completed scrolledIndexChange, so attach() knows to replace it. */
  private wasDetached = false;
  /** Whether the total content size and rendered offset need recomputing from the full geometry. */
  private sizeDirty = true;

  scrolledIndexChange = new Subject<number>();
  /** Emits once the DOM actually reflects the last `setRenderedRange` call. */
  readonly contentRendered = new Subject<void>();

  constructor(defaultItemSize: number) {
    this.geometry = new ListGeometry(defaultItemSize);
  }

  /** Sets the ordered identity of every item, so heights track items rather than indices. */
  setItemKeys(keys: string[]): void {
    this.geometry.setKeys(keys);
    // The held range belongs to the previous data set, so drop it.
    this.renderedRange = { start: 0, end: 0 };
    this.sizeDirty = true;
    this._scheduleUpdate();
  }

  updateItemHeight(key: string, height: number): void {
    if (this.geometry.measure(key, height)) {
      this.sizeDirty = true;
      this._scheduleUpdate();
    }
  }

  /**
   * Drops every cached height. Call this when something changes the height of items that are not
   * rendered.
   *
   * An unrendered item has no DOM node, so no ResizeObserver reports its new height and the cache
   * would keep serving a height measured under the old state. Rendered items report again within a
   * frame or two. The rest measure when they next enter the rendered range.
   */
  invalidateHeights(): void {
    this.geometry.invalidate();
    this.sizeDirty = true;
    this._scheduleUpdate();
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.viewportWidth = this._measureViewportWidth();
    this.emittedIndex = -1;
    // detach() completes scrolledIndexChange, and a completed Subject never emits again. `closed`
    // only reflects unsubscribe(), not complete(), so it can't be used to detect this — track it
    // explicitly instead. A pre-existing subscriber from before the first attach must keep working,
    // so the Subject is only replaced once a detach() has actually completed it.
    if (this.wasDetached) {
      this.scrolledIndexChange = new Subject<number>();
      this.wasDetached = false;
    }
    // The cache from the previous attachment cannot be trusted without a fresh full computation.
    this.sizeDirty = true;
    this._updateViewport();
  }

  detach(): void {
    cancelAnimationFrame(this.pendingFrame);
    this.updateScheduled = false;
    this.scrolledIndexChange.complete();
    this.wasDetached = true;
    this.viewport = null;
  }

  onContentScrolled(): void { this._updateViewport(); }
  onRenderedOffsetChanged(): void {}
  onContentRendered(): void { this.contentRendered.next(); }

  onDataLengthChanged(): void {
    const width = this._measureViewportWidth();
    // CDK calls this for real data-length changes and for every ViewportRuler change.
    //
    // Only a width change invalidates the cache. A rendered item reports its own height change
    // through its ResizeObserver, so the cache only needs a bulk drop for a change the observer
    // cannot see: one that also alters the height of items with no DOM node. Changing the viewport
    // width does that. Invalidating on every call instead left off-screen items permanently on the
    // estimate.
    if (this.viewportWidth && width !== this.viewportWidth) {
      this.geometry.invalidate();
    }
    this.viewportWidth = width;
    // The data length itself is part of the total content size, regardless of whether the width
    // change above also invalidated the geometry.
    this.sizeDirty = true;
    this._updateViewport();
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    this.viewport?.scrollToOffset(this.geometry.offsetAt(index), behavior);
  }

  private _measureViewportWidth(): number {
    return this.viewport?.getElementRef().nativeElement.clientWidth ?? 0;
  }

  /**
   * Whether the range in the DOM has run out of rendered content at either edge.
   *
   * Recomputing on every scroll frame made CDK re-run change detection over every rendered item
   * about once per item height of scrolling. Holding the range until an edge is close spaces that
   * out to once per `MAX_BUFFER_PX - MIN_BUFFER_PX` of travel.
   */
  private _needsNewRange(scrollOffset: number, viewportSize: number, dataLength: number): boolean {
    const { start, end } = this.renderedRange;
    if (start >= end || end > dataLength) return true;

    const renderedTop = this.geometry.offsetAt(start);
    const renderedBottom = renderedTop + this.geometry.spanHeight(start, end);

    return (start > 0 && renderedTop > scrollOffset - MIN_BUFFER_PX)
      || (end < dataLength && renderedBottom < scrollOffset + viewportSize + MIN_BUFFER_PX);
  }

  private _getRangeForCurrentScroll(): ListRange {
    if (!this.viewport) return { start: 0, end: 0 };

    const scrollOffset = this.viewport.measureScrollOffset('top');
    const viewportSize = this.viewport.getViewportSize();
    const dataLength = this.viewport.getDataLength();

    if (dataLength === 0) return { start: 0, end: 0 };
    if (!this._needsNewRange(scrollOffset, viewportSize, dataLength)) return this.renderedRange;

    const { index: start, offset: startOffset } = this.geometry.indexAt(scrollOffset - MAX_BUFFER_PX, dataLength);
    const bottomEdge = scrollOffset + viewportSize + MAX_BUFFER_PX;

    let end = start;
    let visibleBottom = startOffset;

    while (end < dataLength && visibleBottom < bottomEdge) {
      visibleBottom += this.geometry.heightAt(end);
      end++;
    }

    return { start, end: Math.min(dataLength, Math.max(end, start + 1)) };
  }

  /**
   * Collapses a burst of measurements into one update on the next frame.
   *
   * Each mounting item reports its height separately. Without batching, every report ran a full
   * update pass, and each `scrolledIndexChange` emission in that pass triggered app-wide change
   * detection.
   */
  private _scheduleUpdate(): void {
    if (!this.viewport || this.updateScheduled) return;
    this.updateScheduled = true;
    this.pendingFrame = requestAnimationFrame(() => {
      this.updateScheduled = false;
      this._updateViewport();
    });
  }

  private _updateViewport(): void {
    if (!this.viewport) return;
    const range = this._getRangeForCurrentScroll();
    const rangeChanged = range.start !== this.renderedRange.start || range.end !== this.renderedRange.end;
    this.renderedRange = range;

    // Summing the whole geometry costs O(dataLength), so it only runs again when a measurement,
    // key list, or data-length change could have moved the sum — not on every scroll tick.
    if (this.sizeDirty) {
      // The spacer is the viewport's whole layout height, so this call alone positions whatever
      // sits below the viewport. Rounded so sub-pixel values cannot accumulate.
      this.viewport.setTotalContentSize(Math.round(this.geometry.spanHeight(0, this.viewport.getDataLength())));
    }
    this.viewport.setRenderedRange(range);
    // Reissued whenever the range moves, or when a measurement correction above the range moves
    // the offset even though the range itself did not change.
    if (rangeChanged || this.sizeDirty) {
      this.viewport.setRenderedContentOffset(this.geometry.offsetAt(range.start));
    }
    this.sizeDirty = false;

    if (range.start !== this.emittedIndex) {
      this.emittedIndex = range.start;
      this.scrolledIndexChange.next(range.start);
    }
  }
}
